import * as angular from 'angular';
import { IUndoService } from '../../../ng-app/undo/IUndoService';
import { Menu } from '../../typings/Menu';
import { EditableContentScope } from './models';

export class EditableContentController implements angular.IController {
    public static $inject = [
        '$scope',
        'lodash',
        'undoService'
    ];

    /* Passed in via scope */
    public backAction: () => any;
    public cancelAction: () => any;
    public canAlwaysSave: boolean;
    public isReady: boolean = true;
    public currentStepMetadata: any;
    public editFormApi: angular.IFormController;
    public editViewModel: any;
    public hideSaveNextButton: boolean;
    public disableSaveNextButton: boolean;
    public saveAction: () => angular.IPromise<any>;
    public enableUndoOn: string;
    public undoAction: () => any;
    public subMenu: Menu;
    public customAction: () => any;

    /* Set within controller */
    public initialEntityState: any;

    private showSubMenu: boolean;
    private triggerSaveWithErrors: boolean;

    // this should handle cross ng validation
    private customFieldsValidity = {};
    private isCustomFieldsEnabled: boolean;

    constructor(
        private $scope: EditableContentScope,
        private lodash: _.LoDashStatic,
        private undoService: IUndoService
    ) {
    }

    public $onInit() {
        this.saveCurrentState();
        this.showSubMenu = !this.lodash.isEmpty(this.subMenu);
    }

    public $postLink() {
        this.setFormEditApi();
    }

    customValidChange(param, value) {
        this.editViewModel[param] = value;
        this.customFieldsValidity[param] = 'valid';
    }

    customInvalidChange(param, value) {
        this.editViewModel[param] = value;
        this.customFieldsValidity[param] = 'invalid';
    }

    customValidChangeForModule(module, param, value) {
        const index = this.editViewModel.moduleList.indexOf(module);
        this.editViewModel.moduleList[index][param] = value;
        this.customFieldsValidity[EditableContentController.getFieldId(module, param)] = 'valid';
    }

    customInvalidChangeForModule(module, param, value) {
        const index = this.editViewModel.moduleList.indexOf(module);
        this.editViewModel.moduleList[index][param] = value;
        this.customFieldsValidity[EditableContentController.getFieldId(module, param)] = 'invalid';
    }

    setEnabledValidationCustomFields(enable: boolean) {
        this.isCustomFieldsEnabled = enable;
        // return true so we can use this in ng-if
        return true;
    }

    get cancelBtnName() {
        return this.isDirty || this.currentStepMetadata ? 'Cancel' : 'Close';
    }

    get isDirty() {
        return this.$scope.editForm && this.$scope.editForm.$dirty;
    }

    get isSaveEnabled() {
        if (this.hideSaveNextButton) {
            return false;
        }

        // Save should always be enabled if the directive is being used to
        // gather input from multiple related views such as a wizard.
        return this.canAlwaysSave || this.isDirty || this.currentStepMetadata;
    }

    get isDisabled() {
        if (this.disableSaveNextButton === null || this.disableSaveNextButton === undefined) {
            return false;
        }

        return this.disableSaveNextButton;
    }

    public navigateBack() {
        this.backAction();
    }

    public cancel() {
        this.cancelAction();
    }

    public saveChanges() {
        if (!this.triggerSaveWithErrors) {
            this.$scope.editForm.$setSubmitted();

            // If the form is not valid when a save is initiated, make the form dirty so all
            // validation messages are shown for the invalid fields.
            if (this.$scope.editForm.$invalid || (this.isCustomFieldsEnabled && this.isAnyCustomFieldInvalid())) {
                this.$scope.editForm.$setDirty();
                return;
            }
        }

        return this.saveAction().then(() => {
            if (!this.currentStepMetadata && this.$scope.editForm) {
                this.$scope.editForm.$setPristine();
            }

            this.saveCurrentState();
        }).catch(() => {
            // do nothing
        });
    }

    public saveCurrentState() {
        if (this.enableUndoOn) {
            this.initialEntityState = angular.copy(this.editViewModel[this.enableUndoOn]);
        }
    }

    public undoChanges() {
        this.undoService.undo(this.$scope.editForm, this.editViewModel[this.enableUndoOn], this.initialEntityState);

        if (this.lodash.isFunction(this.undoAction)) {
            this.undoAction();
        }
    }

    // Surface the form property values directly on the scope and set
    // higher level status properties specific to this directive.
    private setFormEditApi() {
        this.editFormApi = this.$scope.editForm;
    }

    private isAnyCustomFieldInvalid() {
        return Object.values(this.customFieldsValidity).includes('invalid');
    }

    private static getFieldId(module, param) {
        return param + module.id;
    }
}
